import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { buildMentionMarkup, deriveMentionLabel, parseMentions as parseMentionsUtil } from '@/lib/mention-utils';

interface User {
  id: number;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  email: string | null;
  isOnline: boolean;
  lastSeen: string | null;
}

interface UsersResponse {
  success: boolean;
  data: User[];
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  disabled?: boolean;
  onMention?: (userId: number, userName: string) => void;
  onSubmitShortcut?: () => void;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
}


const buildSubtitle = (user: User) => {
  if (user.displayName && user.username) {
    return `${user.displayName} • ${user.username}`;
  }
  if (user.displayName) return user.displayName;
  if (user.username) return user.username;
  if (user.email) return user.email;
  return 'Team member';
};

export const MentionInput: React.FC<MentionInputProps> = React.memo(({
  value,
  onChange,
  placeholder = 'Type @ to mention someone...',
  multiline = false,
  className,
  disabled = false,
  onMention,
  onSubmitShortcut,
  onClick,
  onFocus,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Internal state for immediate feedback
  const [internalValue, setInternalValue] = useState(value);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Update internal value when prop changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Pre-fetch all users once and cache them
  const { data: allUsersResponse, isLoading: isLoadingAllUsers } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => apiRequest<UsersResponse>('GET', '/api/users/search?q='),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const allUsers = allUsersResponse?.data || [];

  // Filter users client-side for instant results
  const users = useMemo(() => {
    if (!open || !allUsers.length) return [];

    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) return allUsers.slice(0, 10); // Show first 10 users when no search

    return allUsers
      .filter(user => {
        const searchTerms = [
          user.username?.toLowerCase(),
          user.displayName?.toLowerCase(),
          user.email?.toLowerCase()
        ].filter(Boolean);

        return searchTerms.some(term => term?.includes(normalizedSearch));
      })
      .slice(0, 10); // Limit to 10 results for performance
  }, [searchQuery, allUsers, open]);

  const isLoading = isLoadingAllUsers && !allUsers.length;

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart || 0;

    // Update internal value immediately for instant feedback
    setInternalValue(newValue);
    setCursorPosition(newCursorPos);

    // Debounce parent state update for performance
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      onChange(newValue);
    }, 300);

    const lastAtIndex = newValue.lastIndexOf('@', newCursorPos - 1);

    if (lastAtIndex !== -1) {
      const textAfterAt = newValue.substring(lastAtIndex + 1, newCursorPos);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionStartIndex(lastAtIndex);
        setSearchQuery(textAfterAt);
        setOpen(true);
        setSelectedIndex(0);
        return;
      }
    }

    setOpen(false);
    setMentionStartIndex(-1);
    setSearchQuery('');
  }, [onChange]);

  const selectUser = useCallback((user: User) => {
    if (mentionStartIndex === -1) return;

    const beforeMention = internalValue.substring(0, mentionStartIndex);
    const afterCursor = internalValue.substring(cursorPosition);

    const mentionMarkup = buildMentionMarkup(user);
    const newValue = beforeMention + mentionMarkup + afterCursor;

    // Update internal value immediately
    setInternalValue(newValue);

    // Clear any pending debounced update and update parent immediately for mentions
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onChange(newValue);

    const newCursorPos = beforeMention.length + mentionMarkup.length;
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
        inputRef.current.focus();
      }
    }, 0);

    if (onMention) {
      const readableName = user.displayName || user.username || deriveMentionLabel(user);
      onMention(user.id, readableName);
    }

    setOpen(false);
    setMentionStartIndex(-1);
    setSearchQuery('');
    setSelectedIndex(0);
  }, [cursorPosition, mentionStartIndex, onChange, onMention, internalValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === 'NumpadEnter') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      // Flush any pending debounced updates before submitting
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        onChange(internalValue);
      }
      if (onSubmitShortcut) {
        onSubmitShortcut();
      }
      return;
    }

    if (!open || users.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % users.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + users.length) % users.length);
        break;
      case 'Enter':
        e.preventDefault();
        selectUser(users[selectedIndex]);
        break;
      case 'Escape':
        setOpen(false);
        setMentionStartIndex(-1);
        break;
    }
  }, [open, users, selectedIndex, selectUser, onSubmitShortcut, onChange, internalValue]);


  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="relative">
      <InputComponent
        ref={inputRef as any}
        value={internalValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'relative selection:bg-primary/20',
          multiline ? 'min-h-[80px]' : '',
          className
        )}
        onClick={onClick}
        onFocus={onFocus}
      />

      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
          {isLoading ? (
            <div className="p-2 text-sm text-muted-foreground">Loading team…</div>
          ) : users.length > 0 ? (
            <div className="max-h-64 overflow-auto py-1">
              {users.map((user, index) => {
                const initials = deriveMentionLabel(user);
                return (
                  <div
                    key={user.id}
                    className={cn(
                      'flex cursor-pointer select-none items-center gap-3 px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800',
                      index === selectedIndex && 'bg-slate-100 dark:bg-slate-800'
                    )}
                    onClick={() => selectUser(user)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatarUrl || undefined} />
                      <AvatarFallback>{initials.slice(0, 2)}</AvatarFallback>
                    </Avatar>
                    <div className="truncate">
                      <div className="font-medium text-foreground">@{initials}</div>
                      <div className="text-xs text-muted-foreground truncate">{buildSubtitle(user)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-2 text-sm text-muted-foreground">No teammates found</div>
          )}
        </div>
      )}
    </div>
  );
});

MentionInput.displayName = 'MentionInput';

export const parseMentions = parseMentionsUtil;

export const renderMentions = (text: string): React.ReactNode[] => {
  if (!text) return [];

  const mentionRegex = /@\[([^\]]+)\]\((\d+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{text.substring(lastIndex, match.index)}</span>);
    }

    parts.push(
      <span
        key={key++}
        className="rounded bg-primary/10 px-1 font-semibold text-primary"
        data-user-id={match[2]}
      >
        @{match[1]}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(<span key={key++}>{text.substring(lastIndex)}</span>);
  }

  return parts;
};
